import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useContext, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import hasIn from 'lodash/hasIn';
import get from 'lodash/get';
import cn from 'classnames';
import moment from 'moment/moment';
import { ProfileContext } from 'context';
import { decrypt, getKeyFromPem } from 'helpers/crypto';
import { useAction } from 'hooks';
import CloudDrive from 'modules/CloudDrive';
import App from 'modules/App';
import Visit from 'modules/Visit';
import Hcp, { ResendInvitationModal, SetEmailAndSendSharingRequestModal, SyncDataMismatchModal } from 'modules/Hcp';
import Account from 'modules/Account';
import Notifications from 'modules/Notifications';
import AdditionalMeasurements from '../AdditionalMeasurements';
import { ADDITIONAL_MEASUREMENTS_FM } from '../../constants';
import Results from '../Results';
import styles from '../Results/Results.pcss';
import { useSyncData } from './useSyncData';
import { useInitialEffects } from './useInitialEffects';
import FeaturesActions from './FeaturesActions';
import HcpVisitHistoryModal from './HcpVisitHistoryModal';
import HcpVisitNotes from './HcpVisitNotes';
import { getNotesData } from './helpers';
import { renderContextAlerts, renderPatientHeader } from './renderers';
const HcpResults = ({ patientId })=>{
    const { selectors: profileSelectors } = useContext(ProfileContext);
    const phiSet = useSelector(profileSelectors.phiSet);
    const visitNoteFormValues = useSelector(App.selectors.formSelector(Visit.constants.VISIT_NOTE_FORM));
    const visitNotesFloatingModal = useSelector(App.selectors.floatingModalSelector(Visit.constants.VISIT_NOTES_FM));
    const passphrase = useSelector(Account.selectors.passphrase);
    const keyPair = useSelector(Account.selectors.keyPair);
    const isSyncInProgress = useSelector(Hcp.selectors.isSyncInProgress);
    const isSendNotesInProgress = useSelector(CloudDrive.selectors.isSendNotesInProgress);
    const activePatient = useSelector(Hcp.selectors.activePatient);
    const activeClinicMembership = useSelector(Account.selectors.activeClinicMembership);
    const phiSetDocumentId = useSelector(Hcp.selectors.phiSetDocumentId);
    const phiSetPatientId = useSelector(Hcp.selectors.phiSetPatientId);
    const batchesIndex = useSelector(Hcp.selectors.batchesIndex);
    const cgmBatchesIndex = useSelector(Hcp.selectors.cgmBatchesIndex);
    const measurementsBatchesIndex = useSelector(Hcp.selectors.measurementsBatchesIndex);
    const sharingRequest = useSelector(Hcp.selectors.sharingRequest);
    const additionalMeasurementsFM = useSelector(App.selectors.floatingModalSelector(ADDITIONAL_MEASUREMENTS_FM));
    const readings = useSelector(Hcp.selectors.readings);
    const cgmReadings = useSelector(Hcp.selectors.cgmReadings);
    const relatedData = useSelector(Hcp.selectors.relatedData);
    const timeSeriesResources = useSelector(Hcp.selectors.timeSeriesResources);
    const highlightedReadings = useSelector(Hcp.selectors.highlightedReadings);
    const measurements = useSelector(Hcp.selectors.measurements);
    const lastReceivedNotification = useSelector(Notifications.selectors.lastReceivedNotification);
    const activeVisit = useSelector(Visit.selectors.activeVisit);
    const sync = useAction(Hcp.actions.sync);
    const fetchReadings = useAction(CloudDrive.actions.fetchReadings);
    const updatePhiSet = useAction(CloudDrive.actions.updatePhiSet);
    const onOpenFloatingModal = useAction(App.actions.openFloatingModal);
    const onSetFormValues = useAction(App.actions.setFormValues);
    const onSetFormContext = useAction(App.actions.setFormContext);
    const onClearNotesBatches = useAction(Hcp.actions.clearNotesBatches);
    const updateProfile = useAction(Hcp.actions.updatePatient);
    const [startDate] = useState(moment.utc().subtract(3, 'months').startOf('day'));
    const [endDate] = useState(moment.utc().endOf('day'));
    useInitialEffects(patientId);
    const dataToSync = useSyncData(patientId);
    const getNotificationClinicPatientProfileId = (lastNotification)=>{
        const encryptedClinicPatientProfileId = get(lastNotification, 'variables.encryptedClinicPatientProfileId');
        if (!encryptedClinicPatientProfileId) {
            return null;
        }
        try {
            const accountPrvKeyObj = get(keyPair, 'prvKeyPem');
            const accountPrvKey = getKeyFromPem(accountPrvKeyObj, passphrase);
            const { encryptedPrivateKey, encryptedPassphrase } = activeClinicMembership;
            const clinicKeyPassphrase = decrypt(encryptedPassphrase, accountPrvKey);
            const clinicPrvKeyObj = getKeyFromPem(encryptedPrivateKey, clinicKeyPassphrase);
            return decrypt(encryptedClinicPatientProfileId, clinicPrvKeyObj);
        } catch (err) {
            return null;
        }
    };
    const onDataSync = (onlyCloudDrive = false)=>{
        if (dataToSync) {
            sync(dataToSync, onlyCloudDrive);
        }
    };
    useEffect(()=>{
        const activePatientId = get(activePatient, 'id');
        if (lastReceivedNotification && lastReceivedNotification.notificationTrigger === Notifications.constants.NOTIFICATIONS_TRIGGERS.NOTE_WRITTEN_BY_PWD && !isSyncInProgress) {
            const clinicPatientProfileId = getNotificationClinicPatientProfileId(lastReceivedNotification);
            if (clinicPatientProfileId === activePatientId) {
                onClearNotesBatches();
                sync(true);
            }
        }
    }, [
        lastReceivedNotification
    ]);
    useEffect(()=>{
        if (activePatient && phiSetPatientId === patientId && sharingRequest && sharingRequest.invitationCode === activePatient.invitationCode && sharingRequest.sharingStatus === 'Approved') {
            const avatar = get(sharingRequest, 'patient.avatar', null);
            if (avatar) {
                updateProfile(activePatient, {
                    avatar
                }, activeClinicMembership);
            }
            onDataSync();
        }
    }, [
        phiSetPatientId,
        patientId,
        sharingRequest
    ]);
    const onPhiSetUpdate = (entries)=>{
        updatePhiSet(entries, phiSet, phiSetDocumentId, activePatient, Hcp.actions.setPhiSet);
    };
    const onRangeChange = (dateStart, dateEnd)=>{
        if (!activePatient) {
            return;
        }
        const { phiSetReferenceKey, dataType, accessToken, storageProvider } = activePatient;
        fetchReadings({
            dataType,
            phiSet,
            phiSetReferenceKey,
            phiSetDocumentId,
            batchesIndex,
            cgmBatchesIndex,
            measurementsBatchesIndex,
            accessToken,
            storageProvider,
            startDate: dateStart,
            endDate: dateEnd,
            successAction: Hcp.actions.setReadings
        });
    };
    const onAddNote = async (noteType, payload)=>{
        const { values, noteKey, selectedNoteType, payloads } = getNotesData(noteType, payload, visitNoteFormValues);
        if (!visitNotesFloatingModal) {
            await onOpenFloatingModal(Visit.constants.VISIT_NOTES_FM, 'notesContainer', 'leftTop');
        }
        if (!hasIn(values, noteKey)) {
            onSetFormValues(Visit.constants.VISIT_NOTE_FORM, {
                [noteKey]: ''
            });
        }
        onSetFormContext(Visit.constants.VISIT_NOTE_FORM, {
            selectedNoteType,
            payloads
        });
    };
    const renderFeaturesActions = (isDisabled)=>/*#__PURE__*/ _jsx(FeaturesActions, {
            isDisabled: isDisabled
        });
    const getPatientHeaderRenderer = ()=>renderPatientHeader(onDataSync);
    return dataToSync ? /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "row no-gutters flex-nowrap",
                children: [
                    /*#__PURE__*/ _jsx(HcpVisitNotes, {}),
                    /*#__PURE__*/ _jsx("div", {
                        className: cn('col-auto', styles.col, styles.additionalMeasurementsContainer, {
                            [styles['additionalMeasurementsContainer--activeVisit']]: activeVisit,
                            [styles['additionalMeasurementsContainer--hidden']]: !additionalMeasurementsFM
                        }),
                        children: /*#__PURE__*/ _jsx("div", {
                            className: cn('widgets pr-0', {
                                'p-0': activeVisit
                            }),
                            children: /*#__PURE__*/ _jsx(AdditionalMeasurements, {
                                onSuccess: Hcp.actions.setMeasurements
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: cn('col', styles.col),
                        children: /*#__PURE__*/ _jsx(Results, {
                            // TODO get rid of phiSet props drilling. Use ProfileContext instead
                            phiSet: phiSet,
                            phiSetDocumentId: phiSetDocumentId,
                            patient: activePatient,
                            readings: activePatient.dataType === 'CGM' ? cgmReadings : readings,
                            relatedData: relatedData,
                            timeSeriesResources: timeSeriesResources,
                            measurements: measurements,
                            highlightedReadings: highlightedReadings,
                            startDate: startDate,
                            endDate: endDate,
                            standards: dataToSync.standards,
                            isSyncInProgress: isSyncInProgress,
                            renderPatientHeader: getPatientHeaderRenderer(),
                            renderFeaturesActions: renderFeaturesActions,
                            renderContextAlerts: renderContextAlerts,
                            onRangeChange: onRangeChange,
                            onMeasurementsSuccess: Hcp.actions.setPhiSet,
                            onUpdatePhiSet: onPhiSetUpdate,
                            onAddNote: activeVisit && !isSendNotesInProgress ? onAddNote : null
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(SyncDataMismatchModal, {
                isSyncInProgress: isSyncInProgress,
                standards: dataToSync.standards
            }),
            /*#__PURE__*/ _jsx(SetEmailAndSendSharingRequestModal, {}),
            /*#__PURE__*/ _jsx(ResendInvitationModal, {}),
            /*#__PURE__*/ _jsx(HcpVisitHistoryModal, {})
        ]
    }) : null;
};
export default HcpResults;
